import React, { useEffect, useState } from "react";
import supabase from "../../../utils/supabase";
import LessonSequence from "./LessonSequence";
import SceneReader from "./SceneReader";
import { useLaunchParams } from "./shared/useLaunchParams";
import MissingChapter from "./shared/MissingChapter";
import { peT } from "../../../i18n/peLesson";
import { parseDeployment } from "./StudentLessonPage";

/**
 * LTI route wrapper for `/lti/scenes`.
 *
 * This surface mounts the SAME path-agnostic locked-sequence player the native
 * surface uses (`LessonSequence` → `useLessonSequence` → `sequenceEngine` +
 * `peProgress`), imported verbatim — that sameness IS the parity guard. It is no
 * longer a free-scroll SceneReader wrapper; the SceneReader is one step inside
 * the engine-driven sequence (Vocab → Episode → Read Along → Activity).
 *
 * The engine and persistence layers are reused unchanged. The only LTI-specific
 * work is resolving the two persistence keys the shared `peProgress` adapter
 * writes on — `assignment_id` (assignments.id uuid) and `student_id`
 * (auth.users.id) — from the launch context:
 *
 *   The backend (lti.service.ts `provisionPeStudentAssignment`) find-or-creates,
 *   on every student PE launch, a native `assignments` anchor + a
 *   `student_assignments` row, and passes the row's id back as the
 *   `student_assignment_id` launch param. That row carries both keys, so a
 *   single RLS-scoped lookup (the student's own row) yields them. chapter_id /
 *   tier / lang come straight from the launch (chapter_id is already the
 *   pe_chapters PK — see LtiChapterPickerPage).
 *
 * If there is no `student_assignment_id` (e.g. a teacher preview launch) the
 * sequence still renders; persistence is simply disabled (null keys), exactly
 * as `useLessonSequence` already handles for the native path.
 */
const SceneReaderPage: React.FC = () => {
  const { chapterId, tier, lang, role, studentAssignmentId, assets: assetsParam } =
    useLaunchParams();

  // Deployment from the launch (spec §5). The backend packs the teacher's
  // { presetId, assets } emit into custom.value; useSearchParams decodes it once.
  // We run it through the SAME parseDeployment the native StudentLessonPage uses
  // — one parser, no divergence. Absent/malformed ⇒ undefined ⇒ useLessonSequence
  // falls back to FULL_CHAPTER_ASSETS (today's behavior, zero regression).
  const assets = React.useMemo(() => {
    if (!assetsParam) return undefined;
    try {
      return parseDeployment(JSON.parse(assetsParam));
    } catch {
      return undefined;
    }
  }, [assetsParam]);

  const [assignmentId, setAssignmentId] = useState<string | null>(null);
  const [studentId, setStudentId] = useState<string | null>(null);
  const [resolving, setResolving] = useState<boolean>(!!studentAssignmentId);

  useEffect(() => {
    if (!studentAssignmentId) {
      setAssignmentId(null);
      setStudentId(null);
      setResolving(false);
      return;
    }

    let cancelled = false;
    setResolving(true);

    (async () => {
      // RLS scopes student_assignments to the student's own rows, so selecting
      // by id returns this student's row only. We read the two keys the shared
      // peProgress adapter persists against (assignment_id, student_id).
      const { data, error } = await supabase
        .from("student_assignments")
        .select("assignment_id, student_id")
        .eq("id", studentAssignmentId)
        .maybeSingle();

      if (cancelled) return;

      if (error || !data) {
        // Fail-soft: keep null keys → persistence disabled, sequence still runs.
        console.warn(
          "[SceneReaderPage] could not resolve student_assignment for persistence:",
          error,
        );
        setAssignmentId(null);
        setStudentId(null);
      } else {
        setAssignmentId((data as any).assignment_id ?? null);
        setStudentId((data as any).student_id ?? null);
      }
      setResolving(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [studentAssignmentId]);

  if (!chapterId) return <MissingChapter screen="reader" lang={lang} />;

  // §7 parity invariant (d): an LTI INSTRUCTOR launch reaches the SAME 7-tab
  // teacher doc-hub the native path renders (TeacherLessonPreview), by mounting
  // the same SceneReader role="teacher" component on the launch-resolved
  // chapter/tier/lang. No student persistence keys needed (the doc-hub is
  // read-only). Student launches fall through to the locked LessonSequence below.
  if (role === "teacher") {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <SceneReader chapterId={chapterId} tier={tier} lang={lang} role="teacher" embedded />
        </div>
      </div>
    );
  }

  if (resolving) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">{peT(lang).loadingLesson}</p>
        </div>
      </div>
    );
  }

  // Same engine, same shells, same persistence as the native surface. No
  // dashboardPath: there is no native dashboard inside the Canvas iframe.
  return (
    <LessonSequence
      chapterId={chapterId}
      tier={tier}
      lang={lang}
      assignmentId={assignmentId}
      studentId={studentId}
      studentAssignmentId={studentAssignmentId}
      assets={assets}
    />
  );
};

export default SceneReaderPage;
